import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const IPManagementTab = () => {
  const [activeTab, setActiveTab] = useState('whitelist');
  const [newIP, setNewIP] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const [whitelist, setWhitelist] = useState([
    {
      id: 1,
      ip: '192.168.1.0/24',
      description: 'Red corporativa principal',
      addedBy: 'admin@vinpro.mx',
      addedAt: '2024-10-15 14:30:00',
      status: 'active'
    },
    {
      id: 2,
      ip: '10.0.0.0/16',
      description: 'VPN empresarial',
      addedBy: 'admin@vinpro.mx',
      addedAt: '2024-10-10 09:15:00',
      status: 'active'
    }
  ]);

  const [blacklist, setBlacklist] = useState([
    {
      id: 1,
      ip: '203.0.113.0/24',
      description: 'Rango bloqueado por seguridad',
      addedBy: 'security@vinpro.mx',
      addedAt: '2024-10-12 16:45:00',
      status: 'active'
    }
  ]);

  const validateIP = (ip) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?:\/(?:[0-9]|[1-2][0-9]|3[0-2]))?$/;
    return ipRegex?.test(ip);
  };

  const handleAddIP = () => {
    if (!validateIP(newIP)) {
      alert('Por favor ingrese una dirección IP válida (ej: 192.168.1.1 o 192.168.1.0/24)');
      return;
    }

    const newEntry = {
      id: Date.now(),
      ip: newIP,
      description: newDescription || 'Sin descripción',
      addedBy: 'admin@vinpro.mx',
      addedAt: new Date()?.toLocaleString('es-MX'),
      status: 'active'
    };

    if (activeTab === 'whitelist') {
      setWhitelist(prev => [...prev, newEntry]);
    } else {
      setBlacklist(prev => [...prev, newEntry]);
    }

    setNewIP('');
    setNewDescription('');
  };

  const handleRemoveIP = (id) => {
    if (activeTab === 'whitelist') {
      setWhitelist(prev => prev?.filter(item => item?.id !== id));
    } else {
      setBlacklist(prev => prev?.filter(item => item?.id !== id));
    }
  };

  const currentList = activeTab === 'whitelist' ? whitelist : blacklist;

  return (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} className="text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-900">Gestión de Direcciones IP</h4>
            <p className="text-sm text-yellow-700 mt-1">
              Configure las direcciones IP permitidas (whitelist) y bloqueadas (blacklist). Los cambios se aplican inmediatamente y afectan el acceso al sistema.
            </p>
          </div>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('whitelist')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'whitelist' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} />
              <span>Lista Blanca ({whitelist?.length})</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('blacklist')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'blacklist' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Icon name="XCircle" size={16} />
              <span>Lista Negra ({blacklist?.length})</span>
            </div>
          </button>
        </nav>
      </div>
      {/* Add IP Form */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground mb-4">
          Agregar IP a {activeTab === 'whitelist' ? 'Lista Blanca' : 'Lista Negra'}
        </h3>
        
        <div className="grid md:grid-cols-3 gap-4">
          <Input
            label="Dirección IP o Rango"
            type="text"
            value={newIP}
            onChange={(e) => setNewIP(e?.target?.value)}
            placeholder="192.168.1.1 o 192.168.1.0/24"
            description="Formato: IP individual o rango CIDR"
          />
          
          <Input
            label="Descripción"
            type="text"
            value={newDescription}
            onChange={(e) => setNewDescription(e?.target?.value)}
            placeholder="Descripción opcional"
          />
          
          <div className="flex items-end">
            <Button
              variant="default"
              onClick={handleAddIP}
              iconName="Plus"
              iconPosition="left"
              disabled={!newIP}
              className="w-full"
            >
              Agregar
            </Button>
          </div>
        </div>
      </div>
      {/* IP List */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-6 border-b border-border">
          <h3 className="font-semibold text-foreground">
            {activeTab === 'whitelist' ? 'IPs Permitidas' : 'IPs Bloqueadas'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {activeTab === 'whitelist' ?'Solo estas direcciones IP pueden acceder al sistema' :'Estas direcciones IP están bloqueadas del sistema'
            }
          </p>
        </div>

        <div className="divide-y divide-border">
          {currentList?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Globe" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h4 className="font-medium text-foreground mb-2">
                No hay IPs en la {activeTab === 'whitelist' ? 'lista blanca' : 'lista negra'}
              </h4>
              <p className="text-sm text-muted-foreground">
                Agregue direcciones IP para {activeTab === 'whitelist' ? 'permitir' : 'bloquear'} el acceso
              </p>
            </div>
          ) : (
            currentList?.map((item) => (
              <div key={item?.id} className="p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        activeTab === 'whitelist' ? 'bg-green-500' : 'bg-red-500'
                      }`} />
                      <div>
                        <div className="font-mono text-sm font-medium text-foreground">
                          {item?.ip}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {item?.description}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-2 flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>Agregado por: {item?.addedBy}</span>
                      <span>•</span>
                      <span>{item?.addedAt}</span>
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveIP(item?.id)}
                    iconName="Trash2"
                    iconSize={16}
                    className="text-destructive hover:text-destructive"
                  >
                    Eliminar
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* Statistics */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={20} className="text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-foreground">IPs Permitidas</h4>
              <p className="text-sm text-muted-foreground">Acceso autorizado</p>
            </div>
          </div>
          <div className="text-2xl font-bold text-foreground">{whitelist?.length}</div>
          <div className="text-sm text-muted-foreground">rangos configurados</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Icon name="XCircle" size={20} className="text-red-600" />
            </div>
            <div>
              <h4 className="font-semibold text-foreground">IPs Bloqueadas</h4>
              <p className="text-sm text-muted-foreground">Acceso denegado</p>
            </div>
          </div>
          <div className="text-2xl font-bold text-foreground">{blacklist?.length}</div>
          <div className="text-sm text-muted-foreground">rangos bloqueados</div>
        </div>
      </div>
    </div>
  );
};

export default IPManagementTab;